import { HStack, IconButton, Stack, Text } from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import * as React from "react";
import {
  LuALargeSmall,
  LuCalendar,
  LuPencil,
  LuPlus,
  LuTarget,
} from "react-icons/lu";
import { useParams } from "react-router-dom";

import {
  CreateRetargetingRuleData,
  createRetargetingRule,
  deleteRetargetingRule,
  getRetargetingRules,
  updateRetargetingRule,
} from "../api/retargeting-rule";
import { Button } from "../components/button";
import DataTable from "../components/data-table";
import { DeleteButton } from "../components/delete-button";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../components/dialog";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import Page from "../components/page";
import PageBody from "../components/page-body";
import PageHeader from "../components/page-header";
import RetargetingRuleForm, {
  RetargetingRuleFormData,
} from "../components/retargeting-rule-form";
import { useFacebookAdAccounts, useLoading, useToaster } from "../hooks";
import { FacebookAdAccount } from "../types/facebook";
import { RetargetingRule } from "../types/retargeting-rule";

export default function RetargetingPage() {
  const { workspace } = useParams();

  const wrapToast = useToaster();
  const [total, setTotal] = React.useState(0);
  const [rules, setRules] = React.useState<RetargetingRule[]>([]);
  const [isLoadingRules, wrapLoadingRules] = useLoading();
  const loadRules = React.useCallback(async () => {
    await wrapToast(async () => {
      await wrapLoadingRules(async () => {
        const { total, rules } = await getRetargetingRules(workspace);
        setTotal(total);
        setRules(rules);
      });
    });
  }, [workspace, wrapLoadingRules, wrapToast]);
  React.useEffect(() => {
    loadRules();
  }, [loadRules]);

  const {
    facebookAdAccounts,
    isLoadingFacebookAdAccounts,
    loadFacebookAdAccounts,
  } = useFacebookAdAccounts(workspace);
  React.useEffect(() => {
    loadFacebookAdAccounts();
  }, [loadFacebookAdAccounts]);

  const [isCreatingRule, wrapIsCreatingRule] = useLoading();
  const onCreateRule = React.useCallback(
    async (data: CreateRetargetingRuleData) => {
      await wrapToast(async () => {
        await wrapIsCreatingRule(async () => {
          await createRetargetingRule(workspace, data);
          await loadRules();
        });
      });
    },
    [loadRules, wrapIsCreatingRule, wrapToast, workspace],
  );
  const onUpdateRule = React.useCallback(
    async (rule: RetargetingRule) => {
      await wrapToast(async () => {
        await wrapLoadingRules(async () => {
          await updateRetargetingRule(workspace, rule);
          await loadRules();
        });
      });
    },
    [loadRules, wrapLoadingRules, wrapToast, workspace],
  );
  const onDeleteRule = React.useCallback(
    async (rule: RetargetingRule) => {
      await wrapToast(async () => {
        await wrapLoadingRules(async () => {
          await deleteRetargetingRule(workspace, rule.id);
          await loadRules();
        });
      });
    },
    [loadRules, wrapLoadingRules, wrapToast, workspace],
  );

  return (
    <Page loading={isLoadingRules}>
      <Stack flex="1">
        <PageHeader
          title="Retargeting"
          subtitle="Retarget visitors with a high likelihood of conversion or high LTV"
        >
          {total > 0 && (
            <CreateRetargetingRuleDialog
              adAccounts={facebookAdAccounts}
              loading={isCreatingRule}
              loadingAdAccounts={isLoadingFacebookAdAccounts}
              onSubmit={onCreateRule}
              triggerSize="xs"
            />
          )}
        </PageHeader>
        {total === 0 ? (
          <PageBody>
            <HStack flex="1" alignItems="center">
              <EmptyState
                icon={<IconCircle Icon={LuTarget} variant="flat" />}
                title="No Retargeting Rules"
                description="Add your first retargeting rule to get started."
              >
                <CreateRetargetingRuleDialog
                  adAccounts={facebookAdAccounts}
                  loading={isCreatingRule}
                  loadingAdAccounts={isLoadingFacebookAdAccounts}
                  onSubmit={onCreateRule}
                />
              </EmptyState>
            </HStack>
          </PageBody>
        ) : (
          <Stack align="top" flex="1" gap="1">
            <DataTable
              data={rules}
              columns={[
                {
                  id: "name",
                  name: "Name",
                  icon: <LuALargeSmall />,
                  width: 180,
                  cell: ({ value: rule }) => {
                    return <Text color="fg">{rule.name}</Text>;
                  },
                },
                {
                  id: "created-at",
                  name: "Created",
                  icon: <LuCalendar />,
                  width: 180,
                  cell: ({ value: rule }) => {
                    return (
                      <Text>
                        {formatDistanceToNow(rule["created-at"], {
                          addSuffix: true,
                        })}
                      </Text>
                    );
                  },
                },
                {
                  width: 120,
                  cell: ({ value: rule }) => {
                    return (
                      <HStack gap="2">
                        <UpdateRetargetingRuleDialog
                          adAccounts={facebookAdAccounts}
                          loading={isCreatingRule}
                          loadingAdAccounts={isLoadingFacebookAdAccounts}
                          defaultValue={{
                            name: rule.name,
                            rules: rule.rules,
                            fbAdAccountId: [rule["fb-ad-account-id"]],
                          }}
                          onSubmit={({ rules }) =>
                            onUpdateRule({ ...rule, rules })
                          }
                        />
                        <DeleteButton
                          size="2xs"
                          colorPalette="gray"
                          label="Delete Retargeting Rule"
                          message="Delete this Retargeting Rule?"
                          onDelete={() => onDeleteRule(rule)}
                        />
                      </HStack>
                    );
                  },
                },
              ]}
            />
          </Stack>
        )}
      </Stack>
    </Page>
  );
}

interface CreateRetargetingRuleDialogProps {
  adAccounts: FacebookAdAccount[];
  loading?: boolean;
  loadingAdAccounts?: boolean;
  onSubmit: (data: CreateRetargetingRuleData) => void;
  triggerSize?: "xs" | "sm" | "md";
}

function CreateRetargetingRuleDialog({
  adAccounts,
  loading,
  loadingAdAccounts,
  onSubmit,
  triggerSize,
}: CreateRetargetingRuleDialogProps) {
  const dialogContentRef = React.useRef<HTMLDivElement>(null);
  return (
    <DialogRoot size="lg">
      <DialogTrigger asChild>
        <Button size={triggerSize}>
          <LuPlus />
          Create Retargeting Rule
        </Button>
      </DialogTrigger>
      <DialogContent bg="bg.muted" ref={dialogContentRef}>
        <RetargetingRuleForm
          adAccounts={adAccounts}
          loadingAdAccounts={loadingAdAccounts}
          onSubmit={({ name, rules, fbAdAccountId }) =>
            onSubmit({
              name,
              rules,
              "fb-ad-account-id": fbAdAccountId[0],
            })
          }
          portalRef={dialogContentRef}
          wrap={(content) => (
            <>
              <DialogHeader bg="bg" borderTopRadius="md">
                <HStack>
                  <IconCircle Icon={LuTarget} size="md" />
                  <DialogTitle>Create Retargeting Rule</DialogTitle>
                </HStack>
              </DialogHeader>
              <DialogBody bg="bg" borderBottomRadius="md" borderBottomWidth="1">
                {content}
              </DialogBody>
              <DialogFooter pt="4">
                <DialogActionTrigger asChild>
                  <Button colorPalette="gray" variant="surface">
                    Cancel
                  </Button>
                </DialogActionTrigger>
                <Button loading={loading} type="submit">
                  Create
                </Button>
              </DialogFooter>
              <DialogCloseTrigger colorPalette="gray" />
            </>
          )}
        />
      </DialogContent>
    </DialogRoot>
  );
}

interface UpdateRetargetingRuleDialogProps {
  adAccounts: FacebookAdAccount[];
  loading?: boolean;
  loadingAdAccounts?: boolean;
  defaultValue?: RetargetingRuleFormData;
  onSubmit: (data: CreateRetargetingRuleData) => void;
}

function UpdateRetargetingRuleDialog({
  adAccounts,
  loading,
  loadingAdAccounts,
  defaultValue,
  onSubmit,
}: UpdateRetargetingRuleDialogProps) {
  const dialogContentRef = React.useRef<HTMLDivElement>(null);
  return (
    <DialogRoot size="lg">
      <DialogTrigger asChild>
        <IconButton
          aria-label="Edit"
          colorPalette="gray"
          variant="ghost"
          size="xs"
        >
          <LuPencil />
        </IconButton>
      </DialogTrigger>
      <DialogContent bg="bg.muted" ref={dialogContentRef}>
        <RetargetingRuleForm
          adAccounts={adAccounts}
          canChangeAdAccount={false}
          canChangeName={false}
          defaultValue={defaultValue}
          loadingAdAccounts={loadingAdAccounts}
          onSubmit={({ name, rules, fbAdAccountId }) =>
            onSubmit({
              name,
              rules,
              "fb-ad-account-id": fbAdAccountId[0],
            })
          }
          portalRef={dialogContentRef}
          wrap={(content) => (
            <>
              <DialogHeader bg="bg" borderTopRadius="md">
                <HStack>
                  <IconCircle Icon={LuTarget} size="md" />
                  <DialogTitle>Edit Retargeting Rule</DialogTitle>
                </HStack>
              </DialogHeader>
              <DialogBody bg="bg" borderBottomRadius="md" borderBottomWidth="1">
                {content}
              </DialogBody>
              <DialogFooter pt="4">
                <DialogActionTrigger asChild>
                  <Button colorPalette="gray" variant="surface">
                    Cancel
                  </Button>
                </DialogActionTrigger>
                <Button loading={loading} type="submit">
                  Save
                </Button>
              </DialogFooter>
              <DialogCloseTrigger colorPalette="gray" />
            </>
          )}
        />
      </DialogContent>
    </DialogRoot>
  );
}
